import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import { confirmPopup, ConfirmPopup } from 'primereact/confirmpopup';
import { FilterMatchMode } from 'primereact/api';
import axios from 'axios';
import moment from 'moment';
import { GoogleMap, PolylineF, MarkerF, InfoWindowF, useJsApiLoader } from '@react-google-maps/api';
import { OPCIONES_GOOGLE_MAPS } from '../../mapas/opcionesGoogleMaps';
import { BASE_URL } from '../../services/ApiService/ApiConfig';
import { getPantallaActiva } from '../../util/permisosPantalla';
import ReporteDispositivosConfig from './ReporteDispositivosConfig';

const API = `${BASE_URL}/reporte-dispositivo`;

const getUsuarioId = () => localStorage.getItem('userId')?.replaceAll('"', '') || null;
const esAdmin = () => (localStorage.getItem('roles') || '').replaceAll('"', '').includes('ADMINISTRADOR');

// La Configuración la ve el ADMINISTRADOR, o cualquier rol al que se le haya OTORGADO el
// bloque BLOQUE_CONFIGURACION de esta pantalla (vía "Permisos de Roles a Pantallas").
const puedeConfigurar = () => {
    if (esAdmin()) return true;
    const p = getPantallaActiva({ componente: 'ReporteDispositivos' });
    return !!(p && Array.isArray(p.permisosOtorgados) && p.permisosOtorgados.includes('BLOQUE_CONFIGURACION'));
};

const getAuthHeaders = () => {
    const token = localStorage.getItem('jwt')?.replaceAll('"', '') || '';
    return {
        headers: {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Headers': '*',
            Authorization: 'Bearer ' + token
        }
    };
};

/** Repite la clave por cada valor de un arreglo (grupos=a&grupos=b) y codifica todo. */
const serializarParams = (params) => Object.entries(params || {})
    .flatMap(([k, v]) => (Array.isArray(v) ? v.map((x) => [k, x]) : [[k, v]]))
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

const MODOS_TEXTO = [
    { label: 'Comienza con', value: 'INICIA' },
    { label: 'Contiene', value: 'CONTIENE' },
    { label: 'Termina con', value: 'TERMINA' }
];

const ESTADOS = [
    { label: 'OK', value: 'OK' },
    { label: 'Colgado', value: 'COLGADO' },
    { label: 'Sin reporte', value: 'SIN_REPORTE' }
];

const severidadEstado = (estado) => {
    switch (estado) {
        case 'OK': return 'success';
        case 'COLGADO': return 'warning';
        case 'SIN_REPORTE': return 'danger';
        default: return null;
    }
};

const ReporteDispositivos = () => {
    const [cortes, setCortes] = useState([]);
    const [selectedCorte, setSelectedCorte] = useState(null);
    const [detalle, setDetalle] = useState([]);
    const [grupos, setGrupos] = useState([]);

    const [filtroEstados, setFiltroEstados] = useState([]);
    const [filtroDesfaseHoras, setFiltroDesfaseHoras] = useState(null);
    const [filtroGrupos, setFiltroGrupos] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');

    const [umbralHoras, setUmbralHoras] = useState(2);
    const [loading, setLoading] = useState(false);
    const [generando, setGenerando] = useState(false);
    const [showConfig, setShowConfig] = useState(false);

    // Validar estatus de un dispositivo (usar / no usar) — bloque independiente del corte.
    const [dispositivosCombo, setDispositivosCombo] = useState([]);
    const [dispositivoValidar, setDispositivoValidar] = useState(null);
    const [validando, setValidando] = useState(false);
    const [resultadoValidar, setResultadoValidar] = useState(null);

    // Historial de posiciones por rango de fecha (un equipo a la vez) — bloque independiente del corte.
    const HIST_MAX = 5000;
    const [histDispositivo, setHistDispositivo] = useState(null);
    const [histInicio, setHistInicio] = useState(null);
    const [histFin, setHistFin] = useState(null);
    const [histCampo, setHistCampo] = useState('fixtime'); // fixtime | servertime: sobre qué fecha se aplica el rango
    const [histDetalle, setHistDetalle] = useState([]);
    const [histLoading, setHistLoading] = useState(false);
    const [histLimite, setHistLimite] = useState(false);
    const [histBuscado, setHistBuscado] = useState(false);
    const [histFiltro, setHistFiltro] = useState(''); // búsqueda global sobre la tabla del historial
    const [histShowMap, setHistShowMap] = useState(false);   // visor de recorrido en pantalla
    const [histMarkerSel, setHistMarkerSel] = useState(null); // punto seleccionado en el mapa
    const [histPdfLoading, setHistPdfLoading] = useState(false);
    // El PDF va al cliente: por defecto solo la fecha del dispositivo; la del servidor, a pedido.
    const [histPdfServidor, setHistPdfServidor] = useState(false);

    // Equipos sin conexión: se mide sobre tc_devices.lastupdate (la última vez que el equipo
    // habló con Traccar), que es distinto del desfase del corte. Bloque independiente.
    const [scHoras, setScHoras] = useState(24);
    const [scGrupos, setScGrupos] = useState([]);      // varios grupos a la vez ("origen|grupoId")
    const [scTexto, setScTexto] = useState('');        // busca por nombre del equipo
    const [scModo, setScModo] = useState('INICIA');    // INICIA | CONTIENE | TERMINA
    const [scFilas, setScFilas] = useState([]);
    const [scLlaves, setScLlaves] = useState([]);      // llaves de attributes presentes en el resultado
    const [scAtribSel, setScAtribSel] = useState([]);  // cuáles de esas llaves entran al reporte
    const [scResumen, setScResumen] = useState(null);
    const [scLoading, setScLoading] = useState(false);
    const [scBuscado, setScBuscado] = useState(false);
    const [scFiltro, setScFiltro] = useState('');
    const [scJson, setScJson] = useState(null);        // fila cuyos atributos se están previsualizando
    // Filtros por columna de la grilla (se aplican sobre lo ya consultado, sin volver al servidor).
    const [scFiltrosCol, setScFiltrosCol] = useState({
        uniqueid: { value: null, matchMode: FilterMatchMode.CONTAINS },
        deviceName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        grupoNombre: { value: null, matchMode: FilterMatchMode.CONTAINS },
        nearpoint: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const histMapRef = useRef(null);
    // Carga de la API de Google Maps para el visor de recorrido del historial.
    const { isLoaded: histMapLoaded } = useJsApiLoader(OPCIONES_GOOGLE_MAPS);

    const toast = useRef(null);

    useEffect(() => {
        cargarInicial();
    }, []);

    const cargarInicial = async () => {
        try {
            const [resCortes, resGrupos] = await Promise.all([
                axios.get(`${API}/cortes`, getAuthHeaders()),
                axios.get(`${API}/grupos`, getAuthHeaders())
            ]);
            const listaCortes = resCortes.data || [];
            setCortes(listaCortes);
            setGrupos(resGrupos.data || []);
            if (listaCortes.length > 0) {
                setSelectedCorte(listaCortes[0].corteId);
                cargarDetalle(listaCortes[0].corteId);
            }
        } catch (e) {
            mostrarError('No se pudieron cargar los cortes.');
        }
        cargarDispositivosCombo();
    };

    // Lista de todos los dispositivos (Traccar 1 y 2) para validar sin generar corte.
    const cargarDispositivosCombo = async () => {
        try {
            const res = await axios.get(`${API}/dispositivos`, {
                ...getAuthHeaders(), params: { usuarioId: getUsuarioId() }
            });
            setDispositivosCombo(res.data || []);
        } catch (e) {
            // silencioso: el bloque de validación queda vacío si falla
        }
    };

    const cargarDetalle = async (corteId) => {
        if (!corteId) return;
        setLoading(true);
        try {
            const res = await axios.get(`${API}/corte/${corteId}/detalle`, {
                ...getAuthHeaders(), params: { usuarioId: getUsuarioId() }
            });
            setDetalle(res.data || []);
        } catch (e) {
            mostrarError('No se pudo cargar el detalle del corte.');
        } finally {
            setLoading(false);
        }
    };

    const generarCorte = async () => {
        setGenerando(true);
        try {
            const res = await axios.post(`${API}/generar`, null, {
                ...getAuthHeaders(),
                params: { tipo: 'MANUAL', umbralHoras }
            });
            toast.current.show({ severity: 'success', summary: 'Corte generado', detail: `Total ${res.data.totalDispositivos} equipos`, life: 3000 });
            await cargarInicial();
        } catch (e) {
            mostrarError('No se pudo generar el corte.');
        } finally {
            setGenerando(false);
        }
    };

    // Exporta exactamente lo que se ve en pantalla (con los filtros aplicados).
    const exportarExcel = async () => {
        if (!selectedCorte || detalleFiltrado.length === 0) {
            mostrarError('No hay filas para exportar.');
            return;
        }
        try {
            const xlsx = await import('xlsx');
            const filas = detalleFiltrado.map((d) => ({
                'Origen': `Traccar ${d.origen}`,
                'Identificador': d.uniqueid || '',
                'Nombre': d.deviceName || '',
                'Grupo': d.grupoNombre || '',
                'Estado': d.estado || '',
                'Fecha servidor': d.servertime ? moment(d.servertime).format('YYYY-MM-DD HH:mm:ss') : '',
                'Fecha dispositivo': d.fixtime ? moment(d.fixtime).format('YYYY-MM-DD HH:mm:ss') : '',
                'Desfase': d.desfaseTexto || '',
                'Latitud': d.latitude ?? '',
                'Longitud': d.longitude ?? '',
                'Punto cercano': d.nearpoint || '',
                'Ubicacion (Maps)': d.mapsUrl || ''
            }));
            const ws = xlsx.utils.json_to_sheet(filas);
            const wb = { Sheets: { 'Dispositivos': ws }, SheetNames: ['Dispositivos'] };
            const buffer = xlsx.write(wb, { bookType: 'xlsx', type: 'array' });
            const fsMod = await import('file-saver');
            const guardar = fsMod.saveAs || (fsMod.default && fsMod.default.saveAs) || fsMod.default;
            guardar(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
                `reporte_dispositivos_corte${selectedCorte}_${new Date().getTime()}.xlsx`);
        } catch (e) {
            mostrarError('No se pudo exportar el Excel.');
        }
    };

    /**
     * Equipos que dejaron de comunicarse hace más de `scHoras`. El backend lee los dos Traccar,
     * se queda con la comunicación más reciente de cada equipo y devuelve, además de las filas,
     * las llaves de `attributes` que existen en ese resultado.
     */
    const cargarSinConexion = async () => {
        setScLoading(true);
        try {
            const params = { horas: scHoras || 24, usuarioId: getUsuarioId() };
            if (scGrupos.length) params.grupos = scGrupos;
            if (scTexto && scTexto.trim()) {
                params.texto = scTexto.trim();
                params.modo = scModo;
            }
            const res = await axios.get(`${API}/sin-conexion`, {
                ...getAuthHeaders(),
                params,
                // Varios grupos deben viajar como grupos=a&grupos=b (lo que espera Spring); axios
                // 0.19 por defecto los manda como grupos[]=a, que no bindea. De paso, todo valor
                // va codificado con encodeURIComponent.
                paramsSerializer: serializarParams
            });
            const data = res.data || {};
            const llaves = data.llavesAtributos || [];
            setScFilas(data.filas || []);
            setScLlaves(llaves);
            // Si un atributo elegido ya no existe en el nuevo resultado, se descarta solo.
            setScAtribSel((prev) => prev.filter((k) => llaves.includes(k)));
            setScResumen({
                fechaConsulta: data.fechaConsulta,
                corte: data.corte,
                horasUmbral: data.horasUmbral,
                totalEvaluados: data.totalEvaluados,
                totalSinConexion: data.totalSinConexion,
                totalNuncaReporto: data.totalNuncaReporto
            });
            setScBuscado(true);
        } catch (e) {
            mostrarError('No se pudieron consultar los equipos sin conexión.');
        } finally {
            setScLoading(false);
        }
    };

    /** Excel de los sin conexión: columnas fijas + un columna por atributo seleccionado. */
    const exportarSinConexion = async () => {
        if (!scFilasFiltradas.length) {
            mostrarError('No hay filas para exportar.');
            return;
        }
        try {
            const xlsx = await import('xlsx');
            const filas = scFilasFiltradas.map((d) => {
                const fila = {
                    'Origen': `Traccar ${d.origen}`,
                    'Identificador': d.uniqueid || '',
                    'Nombre': d.deviceName || '',
                    'Grupo': d.grupoNombre || '',
                    'Ultima conexion': d.lastupdate ? moment(d.lastupdate).format('YYYY-MM-DD HH:mm:ss') : 'Nunca reporto',
                    'Sin conexion hace': d.sinConexionTexto || '',
                    'Horas sin conexion': d.sinConexionHoras ?? '',
                    'Fecha servidor': d.servertime ? moment(d.servertime).format('YYYY-MM-DD HH:mm:ss') : '',
                    'Fecha dispositivo': d.fixtime ? moment(d.fixtime).format('YYYY-MM-DD HH:mm:ss') : '',
                    'Modelo': d.model || '',
                    'Telefono': d.phone || '',
                    'Categoria': d.category || '',
                    'Contacto': d.contact || '',
                    'Latitud': d.latitude ?? '',
                    'Longitud': d.longitude ?? '',
                    'Punto cercano': d.nearpoint || '',
                    'Ubicacion (Maps)': d.mapsUrl || ''
                };
                // Sólo los atributos elegidos entran al archivo: si no se eligió ninguno, no van.
                scAtribSel.forEach((k) => { fila[k] = (d.atributos && d.atributos[k]) || ''; });
                return fila;
            });
            const ws = xlsx.utils.json_to_sheet(filas);
            const wb = { Sheets: { 'Sin conexion': ws }, SheetNames: ['Sin conexion'] };
            const buffer = xlsx.write(wb, { bookType: 'xlsx', type: 'array' });
            const fsMod = await import('file-saver');
            const guardar = fsMod.saveAs || (fsMod.default && fsMod.default.saveAs) || fsMod.default;
            guardar(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
                `dispositivos_sin_conexion_${new Date().getTime()}.xlsx`);
        } catch (e) {
            mostrarError('No se pudo exportar el Excel.');
        }
    };

    const enviarCorreo = (event) => {
        confirmPopup({
            target: event.currentTarget,
            message: 'Se enviara el reporte por correo a los usuarios configurados. Continuar?',
            icon: 'pi pi-envelope',
            accept: async () => {
                try {
                    const res = await axios.post(`${API}/corte/${selectedCorte}/enviar`, null, getAuthHeaders());
                    toast.current.show({ severity: 'success', summary: 'Correos enviados', detail: `${res.data} destinatario(s)`, life: 3000 });
                } catch (e) {
                    mostrarError('No se pudo enviar el correo.');
                }
            }
        });
    };

    const mostrarError = (detail) => {
        toast.current?.show({ severity: 'error', summary: 'Error', detail, life: 3000 });
    };

    // Valida en vivo si el dispositivo seleccionado está reportando (usar / no usar).
    const validarEstatusDispositivo = async () => {
        if (!dispositivoValidar) { mostrarError('Seleccione un dispositivo.'); return; }
        try {
            setValidando(true);
            setResultadoValidar(null);
            const res = await axios.get(`${API}/validar-estatus`, {
                ...getAuthHeaders(),
                params: { identificador: dispositivoValidar.uniqueid, nombre: dispositivoValidar.deviceName || '' }
            });
            setResultadoValidar(res.data);
        } catch (e) {
            setResultadoValidar({ usar: false, mensaje: 'No se pudo validar el estatus.' });
        } finally {
            setValidando(false);
        }
    };

    // Trae el historial de posiciones del equipo elegido en el rango, filtrando por el campo elegido (fixtime/servertime).
    const histFitBounds = () => {
        if (histMapRef.current && histPosicionesMapa.length > 0 && window.google) {
            const bounds = new window.google.maps.LatLngBounds();
            histPosicionesMapa.forEach((p) => bounds.extend({ lat: Number(p.latitude), lng: Number(p.longitude) }));
            histMapRef.current.fitBounds(bounds);
        }
    };

    /** Descarga el informe PDF (portada + resumen + mapa + detalle) generado en el backend. */
    const exportarHistorialPdf = async () => {
        if (!histDispositivo) { mostrarError('Seleccione un dispositivo.'); return; }
        if (!histInicio || !histFin) { mostrarError('Seleccione el rango de fechas.'); return; }
        setHistPdfLoading(true);
        try {
            const res = await axios.get(`${API}/historial/pdf`, {
                ...getAuthHeaders(),
                responseType: 'blob',
                params: {
                    identificador: histDispositivo.uniqueid,
                    fechaInicio: moment(histInicio).format('YYYY-MM-DD HH:mm:ss'),
                    fechaFin: moment(histFin).format('YYYY-MM-DD HH:mm:ss'),
                    maxRows: HIST_MAX,
                    usuarioId: getUsuarioId(),
                    campo: histCampo,
                    nombre: histDispositivo.nombre || histDispositivo.deviceName || '',
                    grupo: histDispositivo.grupoNombre || '',
                    incluirServidor: histPdfServidor
                }
            });
            if (!res.data || res.data.size === 0) {
                mostrarError('No hay posiciones en el rango para generar el informe.');
                return;
            }
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.setAttribute('download', `informe_recorrido_${histDispositivo.uniqueid}.pdf`);
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            mostrarError('No se pudo generar el informe PDF.');
        } finally {
            setHistPdfLoading(false);
        }
    };

    const verHistorial = async () => {
        if (!histDispositivo) { mostrarError('Seleccione un dispositivo.'); return; }
        if (!histInicio || !histFin) { mostrarError('Seleccione el rango de fechas (desde / hasta).'); return; }
        if (histFin < histInicio) { mostrarError('La fecha "hasta" debe ser posterior a "desde".'); return; }
        setHistLoading(true);
        setHistBuscado(true);
        setHistFiltro('');
        try {
            const res = await axios.get(`${API}/historial`, {
                ...getAuthHeaders(),
                params: {
                    identificador: histDispositivo.uniqueid,
                    fechaInicio: moment(histInicio).format('YYYY-MM-DD HH:mm:ss'),
                    fechaFin: moment(histFin).format('YYYY-MM-DD HH:mm:ss'),
                    maxRows: HIST_MAX,
                    usuarioId: getUsuarioId(),
                    campo: histCampo
                }
            });
            const data = res.data || [];
            setHistDetalle(data);
            setHistLimite(data.length >= HIST_MAX);
        } catch (e) {
            setHistDetalle([]);
            mostrarError('No se pudo cargar el historial de posiciones.');
        } finally {
            setHistLoading(false);
        }
    };

    // Exporta el historial mostrado (respetando la búsqueda) a Excel.
    const exportarHistorialExcel = async () => {
        if (histDetalleFiltrado.length === 0) { mostrarError('No hay filas de historial para exportar.'); return; }
        try {
            const xlsx = await import('xlsx');
            const filas = histDetalleFiltrado.map((d) => ({
                'Origen': `Traccar ${d.origen}`,
                'Identificador': d.uniqueid || '',
                'Grupo': d.grupoNombre || '',
                'Fecha servidor': d.servertime ? moment(d.servertime).format('YYYY-MM-DD HH:mm:ss') : '',
                'Fecha dispositivo': d.fixtime ? moment(d.fixtime).format('YYYY-MM-DD HH:mm:ss') : '',
                'Desfase': d.desfaseTexto || '',
                'Velocidad (km/h)': d.velocidadKmh ?? '',
                'Latitud': d.latitude ?? '',
                'Longitud': d.longitude ?? '',
                'Punto cercano': d.nearpoint || '',
                'Ubicacion (Maps)': d.mapsUrl || ''
            }));
            const ws = xlsx.utils.json_to_sheet(filas);
            const wb = { Sheets: { 'Historial': ws }, SheetNames: ['Historial'] };
            const buffer = xlsx.write(wb, { bookType: 'xlsx', type: 'array' });
            const fsMod = await import('file-saver');
            const guardar = fsMod.saveAs || (fsMod.default && fsMod.default.saveAs) || fsMod.default;
            const idEquipo = histDispositivo?.uniqueid || 'equipo';
            guardar(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
                `historial_${idEquipo}_${new Date().getTime()}.xlsx`);
        } catch (e) {
            mostrarError('No se pudo exportar el Excel del historial.');
        }
    };

    // ---- filtros cliente (todos combinables) ----
    const texto = (globalFilter || '').trim().toLowerCase();
    const detalleFiltrado = detalle.filter((d) => {
        // Estado: multi (vacio = todos)
        if (filtroEstados.length > 0 && !filtroEstados.includes(d.estado)) return false;
        // Desfase mayor a X horas
        if (filtroDesfaseHoras && (d.desfaseSegundos == null || d.desfaseSegundos < filtroDesfaseHoras * 3600)) return false;
        // Grupo: multi, por clave compuesta origen|grupoId (los ids chocan entre Traccar 1 y 2)
        if (filtroGrupos.length > 0 && !filtroGrupos.includes(`${d.origen}|${d.grupoId}`)) return false;
        // Busqueda global
        if (texto) {
            const blob = `${d.uniqueid || ''} ${d.deviceName || ''} ${d.grupoNombre || ''} ${d.nearpoint || ''} ${d.estado || ''}`.toLowerCase();
            if (!blob.includes(texto)) return false;
        }
        return true;
    });

    // Búsqueda global sobre la tabla del historial (cliente): matchea origen, identificador, grupo,
    // punto cercano, desfase y ambas fechas formateadas.
    const histTexto = (histFiltro || '').trim().toLowerCase();
    const histDetalleFiltrado = histTexto
        ? histDetalle.filter((d) => {
            const blob = `traccar ${d.origen} ${d.uniqueid || ''} ${d.grupoNombre || ''} ${d.nearpoint || ''} ${d.desfaseTexto || ''} `
                + `${d.servertime ? moment(d.servertime).format('YYYY-MM-DD HH:mm:ss') : ''} `
                + `${d.fixtime ? moment(d.fixtime).format('YYYY-MM-DD HH:mm:ss') : ''}`;
            return blob.toLowerCase().includes(histTexto);
        })
        : histDetalle;

    // Posiciones con coordenadas validas: son las que se pueden dibujar en el mapa.
    const histPosicionesMapa = histDetalleFiltrado.filter(
        (d) => d.latitude != null && d.longitude != null &&
               !isNaN(parseFloat(d.latitude)) && !isNaN(parseFloat(d.longitude)));

    const corteHeader = cortes.find((c) => c.corteId === selectedCorte);

    const opcionesCortes = cortes.map((c) => ({
        label: `#${c.corteId} - ${c.tipo} - ${moment(c.fechaCorte).format('YYYY-MM-DD HH:mm')}`,
        value: c.corteId
    }));

    const opcionesGrupos = grupos.map((g) => ({
        label: `${g.nombre} (Traccar ${g.origen})`,
        value: `${g.origen}|${g.traccarGrupoId}`
    }));

    // ---- templates ----
    const fechaTpl = (campo) => (row) => row[campo] ? moment(row[campo]).format('YYYY-MM-DD HH:mm:ss') : '-';
    // Origen como etiqueta de color para distinguir de un vistazo Traccar 1 vs Traccar 2.
    const origenTpl = (row) => <Tag value={`Traccar ${row.origen}`} severity={row.origen === 2 ? 'info' : 'warning'} />;
    const estadoTpl = (row) => <Tag value={row.estado} severity={severidadEstado(row.estado)} />;
    const desfaseTpl = (row) => row.desfaseTexto || '-';
    const mapsTpl = (row) => row.mapsUrl
        ? <a href={row.mapsUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1a73e8', fontWeight: 'bold' }}><i className="pi pi-map-marker" style={{ marginRight: 4 }}></i>Mapa</a>
        : '-';

    /** Velocidad en km/h (Traccar la guarda en nudos; el backend ya la convierte). */
    const velocidadTpl = (row) => {
        if (row.velocidadKmh == null) return '-';
        const v = Number(row.velocidadKmh);
        // >90 km/h se resalta: sirve para detectar excesos de un vistazo.
        const sev = v > 90 ? 'danger' : v > 1 ? 'info' : null;
        const txt = `${v.toFixed(0)} km/h`;
        return sev ? <Tag value={txt} severity={sev} /> : <span className="text-color-secondary">{txt}</span>;
    };

    const coordTpl = (v) => (v == null ? '-' : Number(v).toFixed(6));

    // ---- sin conexión ----
    // Búsqueda global + filtros por columna. Se replican acá (y no sólo dentro del DataTable) para
    // que el Excel exporte exactamente las filas que se están viendo.
    const scFilasFiltradas = scFilas.filter((d) => {
        if (scFiltro) {
            const t = scFiltro.toLowerCase();
            const hay = [d.uniqueid, d.deviceName, d.grupoNombre, d.nearpoint, d.model, d.phone, d.attributesRaw]
                .some((v) => (v || '').toString().toLowerCase().includes(t));
            if (!hay) return false;
        }
        return Object.entries(scFiltrosCol).every(([campo, f]) => {
            const v = f && f.value;
            if (!v) return true;
            return (d[campo] || '').toString().toLowerCase().includes(v.toString().toLowerCase());
        });
    });

    /** Última conexión; los que nunca reportaron se marcan aparte porque no tienen fecha contra qué medir. */
    const scUltimaTpl = (row) => (row.lastupdate
        ? moment(row.lastupdate).format('YYYY-MM-DD HH:mm:ss')
        : <Tag value="Nunca reportó" severity="danger" />);

    const scSilencioTpl = (row) => {
        if (row.lastupdate == null) return '-';
        const h = row.sinConexionHoras || 0;
        return <Tag value={row.sinConexionTexto || '-'} severity={h >= 72 ? 'danger' : h >= 24 ? 'warning' : 'info'} />;
    };

    /** Celda de atributos: cuántos hay y un acceso a la previsualización del JSON. */
    const scAtributosTpl = (row) => {
        const n = row.atributos ? Object.keys(row.atributos).length : 0;
        if (!n && !row.attributesRaw) return <span className="text-color-secondary">-</span>;
        return (
            <Button className="p-button-text p-button-sm" icon="pi pi-code"
                label={n ? `${n} atributo${n === 1 ? '' : 's'}` : 'ver'}
                onClick={() => setScJson(row)} />
        );
    };

    /** JSON indentado para la previsualización; si el texto no era JSON válido se muestra crudo. */
    const jsonBonito = (raw) => {
        if (!raw) return '';
        try {
            return JSON.stringify(JSON.parse(raw), null, 2);
        } catch (e) {
            return raw;
        }
    };

    // Opciones del combo "Validar dispositivo específico": TODOS los dispositivos
    // (Traccar 1 y 2), independiente del corte y ya filtrados por rol/VIP en el backend.
    const opcionesDispositivos = dispositivosCombo.map((d) => ({
        label: `[Traccar ${d.origen}]  ${d.grupoNombre || '(sin grupo)'}  ·  ${d.nombre || '(sin nombre)'} — ${d.identificador || ''}`,
        value: { uniqueid: d.identificador, deviceName: d.nombre, origen: d.origen }
    }));

    const header = (
        <div className="flex flex-column md:flex-row md:align-items-center justify-content-between gap-3">
            <div className="flex align-items-center gap-2 flex-wrap">
                <Button icon="pi pi-file-excel" label="Excel" className="p-button-success p-button-sm" onClick={exportarExcel} disabled={!selectedCorte} />
                <Button icon="pi pi-envelope" label="Enviar correo" className="p-button-help p-button-sm" onClick={enviarCorreo} disabled={!selectedCorte} />
            </div>
            <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                <MultiSelect value={filtroEstados} options={ESTADOS} onChange={(e) => setFiltroEstados(e.value)}
                    placeholder="Estado" display="chip" showClear style={{ minWidth: '12rem' }} />
                <span className="p-inputgroup" style={{ width: '13rem' }}>
                    <span className="p-inputgroup-addon">Desfase &gt;</span>
                    <InputNumber value={filtroDesfaseHoras} onValueChange={(e) => setFiltroDesfaseHoras(e.value)} suffix=" h" min={0} placeholder="horas" />
                </span>
                <MultiSelect value={filtroGrupos} options={opcionesGrupos} onChange={(e) => setFiltroGrupos(e.value)}
                    placeholder="Grupos" filter display="chip" showClear style={{ minWidth: '14rem', maxWidth: '24rem' }} />
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText type="search" value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Búsqueda..." />
                </span>
            </div>
        </div>
    );

    return (
        <div className="grid">
            <Toast ref={toast} />
            <ConfirmPopup />
            <div className="col-12" style={{ order: 3 }}>
                <div className="card">
                    <h5 className="mb-1">Dispositivos sin conexión</h5>
                    <p className="text-sm text-color-secondary mt-0">
                        Equipos que <b>no se comunican con Traccar</b> desde hace más del tiempo indicado — se mide sobre
                        la última conexión del equipo (<b>lastupdate</b>), no sobre el desfase del corte. Se consultan
                        Traccar 1 y 2, y de cada equipo se toma su comunicación <b>más reciente</b> entre ambos, para no
                        reportar como caído a un equipo migrado que sigue reportando en el otro.
                    </p>
                    <div className="flex align-items-end gap-3 flex-wrap mb-3">
                        <div className="field mb-0">
                            <label htmlFor="scHoras">Sin conexión hace más de</label>
                            <InputNumber id="scHoras" value={scHoras} onValueChange={(e) => setScHoras(e.value)}
                                min={1} suffix=" h" style={{ width: '9rem' }} />
                        </div>
                        <div className="field mb-0">
                            <label>Grupos</label>
                            <MultiSelect value={scGrupos} options={opcionesGrupos} onChange={(e) => setScGrupos(e.value)}
                                filter display="chip" showClear placeholder="Todos los grupos"
                                style={{ minWidth: '18rem', maxWidth: '28rem' }} />
                        </div>
                        <div className="field mb-0">
                            <label>Nombre del equipo</label>
                            <div className="p-inputgroup" style={{ width: '24rem' }}>
                                <Dropdown value={scModo} options={MODOS_TEXTO} onChange={(e) => setScModo(e.value)}
                                    style={{ width: '10rem' }} />
                                <InputText value={scTexto} onChange={(e) => setScTexto(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') cargarSinConexion(); }}
                                    placeholder="ej: DO" />
                            </div>
                        </div>
                        <div className="field mb-0">
                            <Button label="Consultar" icon="pi pi-search" className="p-button-primary"
                                onClick={cargarSinConexion} loading={scLoading} />
                        </div>
                        <div className="field mb-0">
                            <Button icon="pi pi-file-excel" label="Excel" className="p-button-success p-button-outlined"
                                onClick={exportarSinConexion} disabled={!scFilasFiltradas.length} />
                        </div>
                    </div>

                    {scResumen && (
                        <div className="flex gap-2 mb-3 flex-wrap align-items-center">
                            <Tag value={`Sin conexión: ${scResumen.totalSinConexion}`} severity="danger" />
                            <Tag value={`Evaluados: ${scResumen.totalEvaluados}`} />
                            {scResumen.totalNuncaReporto > 0 &&
                                <Tag value={`Nunca reportaron: ${scResumen.totalNuncaReporto}`} severity="warning" />}
                            <span className="text-xs text-color-secondary">
                                Consultado {moment(scResumen.fechaConsulta).format('YYYY-MM-DD HH:mm:ss')} · se listan
                                los equipos cuya última conexión es anterior a {moment(scResumen.corte).format('YYYY-MM-DD HH:mm:ss')}
                            </span>
                        </div>
                    )}

                    {scBuscado && (
                        <>
                            <div className="flex align-items-center gap-2 flex-wrap mb-2 justify-content-between">
                                <span className="p-inputgroup" style={{ maxWidth: '32rem' }}>
                                    <span className="p-inputgroup-addon"><i className="pi pi-list" /></span>
                                    <MultiSelect value={scAtribSel} options={scLlaves.map((k) => ({ label: k, value: k }))}
                                        onChange={(e) => setScAtribSel(e.value)} filter display="chip" showClear
                                        placeholder={scLlaves.length ? 'Atributos a incluir en el reporte' : 'Sin atributos en el resultado'}
                                        disabled={!scLlaves.length} style={{ minWidth: '20rem' }} />
                                </span>
                                <span className="p-input-icon-left">
                                    <i className="pi pi-search" />
                                    <InputText type="search" value={scFiltro} onChange={(e) => setScFiltro(e.target.value)}
                                        placeholder="Búsqueda..." />
                                </span>
                            </div>
                            <p className="text-xs text-color-secondary mt-0 mb-2">
                                Los atributos que elijas se agregan como columnas y viajan al Excel; los que dejes fuera
                                no salen en el reporte. El JSON completo siempre se puede ver en la columna Atributos.
                            </p>
                            <DataTable value={scFilasFiltradas} paginator rows={20} rowsPerPageOptions={[10, 20, 50, 100]}
                                loading={scLoading} responsiveLayout="scroll"
                                filters={scFiltrosCol} filterDisplay="row" onFilter={(e) => setScFiltrosCol(e.filters)}
                                emptyMessage="No hay equipos sin conexión con ese tiempo.">
                                <Column body={origenTpl} header="Origen" style={{ minWidth: '90px' }} />
                                <Column field="uniqueid" header="Identificador" sortable filter showFilterMenu={false}
                                    filterPlaceholder="Buscar" style={{ minWidth: '150px' }} />
                                <Column field="deviceName" header="Nombre" sortable filter showFilterMenu={false}
                                    filterPlaceholder="Buscar" style={{ minWidth: '170px' }} />
                                <Column field="grupoNombre" header="Grupo" sortable filter showFilterMenu={false}
                                    filterPlaceholder="Buscar" style={{ minWidth: '160px' }} />
                                <Column field="lastupdate" header="Última conexión" body={scUltimaTpl} sortable style={{ minWidth: '160px' }} />
                                <Column field="sinConexionHoras" header="Sin conexión hace" body={scSilencioTpl} sortable style={{ minWidth: '150px' }} />
                                <Column field="fixtime" header="Fecha dispositivo" body={fechaTpl('fixtime')} sortable style={{ minWidth: '150px' }} />
                                <Column field="nearpoint" header="Punto cercano" sortable filter showFilterMenu={false}
                                    filterPlaceholder="Buscar" style={{ minWidth: '190px' }} />
                                <Column header="Ubicación" body={mapsTpl} style={{ minWidth: '90px' }} />
                                <Column header="Atributos" body={scAtributosTpl} style={{ minWidth: '130px' }} />
                                {scAtribSel.map((k) => (
                                    <Column key={k} header={k} sortable style={{ minWidth: '140px' }}
                                        body={(row) => (row.atributos && row.atributos[k]) || '-'} />
                                ))}
                            </DataTable>
                        </>
                    )}
                </div>
            </div>

            <Dialog header={`Atributos · ${scJson?.deviceName || scJson?.uniqueid || ''}`} visible={!!scJson}
                style={{ width: '46rem', maxWidth: '95vw' }} onHide={() => setScJson(null)} dismissableMask>
                {scJson && (
                    <>
                        {scJson.atributos && Object.keys(scJson.atributos).length > 0 && (
                            <DataTable value={Object.entries(scJson.atributos).map(([k, v]) => ({ llave: k, valor: v }))}
                                responsiveLayout="scroll" className="mb-3" size="small">
                                <Column field="llave" header="Atributo" style={{ width: '40%' }} />
                                <Column field="valor" header="Valor" />
                            </DataTable>
                        )}
                        <h6 className="mb-2">JSON original</h6>
                        <pre style={{
                            background: '#1e1e1e', color: '#d4d4d4', padding: '1rem', borderRadius: 6,
                            fontSize: '0.8rem', lineHeight: 1.5, maxHeight: '22rem', overflow: 'auto',
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0
                        }}>{jsonBonito(scJson.attributesRaw) || '(sin atributos)'}</pre>
                    </>
                )}
            </Dialog>

            <div className="col-12" style={{ order: 4 }}>
                <div className="card">
                    <h4>Reporte de Dispositivos por Corte</h4>
                    <div className="flex flex-column md:flex-row md:align-items-end gap-3">
                        <div className="field mb-0">
                            <label htmlFor="corte">Corte</label>
                            <Dropdown id="corte" value={selectedCorte} options={opcionesCortes} onChange={(e) => { setSelectedCorte(e.value); cargarDetalle(e.value); }}
                                placeholder="Seleccione un corte" style={{ minWidth: '22rem' }} />
                        </div>
                        <div className="field mb-0">
                            <label htmlFor="umbral">
                                Umbral (horas)
                                <i className="pi pi-info-circle umbral-help" style={{ marginLeft: 6, color: '#6366F1', cursor: 'pointer' }} />
                            </label>
                            <Tooltip target=".umbral-help" position="bottom" style={{ maxWidth: '340px' }}>
                                <div style={{ lineHeight: 1.5 }}>
                                    <b>Umbral (horas):</b> atraso máximo tolerado antes de marcar un equipo como problemático.
                                    <br />🟢 <b>OK</b>: el desfase está dentro del umbral.
                                    <br />🟡 <b>Colgado</b>: comunica, pero su último fix GPS está atrasado más que el umbral.
                                    <br />🔴 <b>Sin reporte</b>: dejó de comunicar hace más que el umbral.
                                    <br />Menor umbral = más estricto (marca más equipos); mayor umbral = más tolerante.
                                </div>
                            </Tooltip>
                            <InputNumber id="umbral" value={umbralHoras} onValueChange={(e) => setUmbralHoras(e.value)} min={0} suffix=" h" style={{ width: '8rem' }} />
                        </div>
                        <div className="field mb-0">
                            <Button label="Generar corte ahora" icon="pi pi-bolt" className="p-button-primary" onClick={generarCorte} loading={generando} />
                        </div>
                        {puedeConfigurar() && (
                            <div className="field mb-0">
                                <Button label="Configuración" icon="pi pi-cog" className="p-button-secondary p-button-outlined" onClick={() => setShowConfig(true)} />
                            </div>
                        )}
                    </div>

                    {corteHeader && (
                        <div className="flex gap-2 mt-3 flex-wrap">
                            <Tag value={`Total: ${corteHeader.totalDispositivos}`} />
                            <Tag value={`OK: ${corteHeader.totalOk}`} severity="success" />
                            <Tag value={`Colgados: ${corteHeader.totalColgado}`} severity="warning" />
                            <Tag value={`Sin reporte: ${corteHeader.totalSinReporte}`} severity="danger" />
                            <Tag value={`Umbral: ${corteHeader.umbralHoras} h`} severity="info" />
                        </div>
                    )}

                    <h6 className="mt-4 mb-2">Detalle del corte seleccionado</h6>
                    <DataTable value={detalleFiltrado} paginator rows={20} rowsPerPageOptions={[10, 20, 50, 100]}
                        loading={loading} emptyMessage="No hay equipos para mostrar." responsiveLayout="scroll"
                        header={header} sortField="desfaseSegundos" sortOrder={-1}>
                        <Column body={origenTpl} header="Origen" style={{ minWidth: '90px' }} />
                        <Column field="uniqueid" header="Identificador" sortable style={{ minWidth: '140px' }} />
                        <Column field="deviceName" header="Nombre" sortable style={{ minWidth: '160px' }} />
                        <Column field="grupoNombre" header="Grupo" sortable style={{ minWidth: '150px' }} />
                        <Column field="estado" header="Estado" body={estadoTpl} sortable style={{ minWidth: '110px' }} />
                        <Column field="servertime" header="Fecha servidor" body={fechaTpl('servertime')} sortable style={{ minWidth: '150px' }} />
                        <Column field="fixtime" header="Fecha dispositivo" body={fechaTpl('fixtime')} sortable style={{ minWidth: '150px' }} />
                        <Column field="desfaseSegundos" header="Desfase" body={desfaseTpl} sortable style={{ minWidth: '110px' }} />
                        <Column field="nearpoint" header="Punto cercano" sortable style={{ minWidth: '180px' }} />
                        <Column header="Ubicación" body={mapsTpl} style={{ minWidth: '90px' }} />
                    </DataTable>
                </div>
            </div>

            <div className="col-12" style={{ order: 1 }}>
                <div className="card">
                    <h5 className="mb-1">Validar dispositivo específico</h5>
                    <p className="text-sm text-color-secondary mt-0">Validá el estatus GPS de cualquier equipo (Traccar 1 y 2) sin necesidad de generar un corte.</p>
                    <div className="field mb-3">
                        <label>Dispositivo</label>
                        <Dropdown value={dispositivoValidar} options={opcionesDispositivos} onChange={(e) => { setDispositivoValidar(e.value); setResultadoValidar(null); }}
                            filter showClear placeholder="Seleccione un dispositivo..." className="w-full" />
                    </div>
                    <div className="flex align-items-center gap-3 flex-wrap">
                        <Button icon="pi pi-map-marker" label="Validar estatus" className="p-button-outlined p-button-secondary"
                            onClick={validarEstatusDispositivo} loading={validando} disabled={!dispositivoValidar} />
                        {resultadoValidar && (
                            <div className="flex align-items-center gap-2 flex-wrap">
                                <Tag value={resultadoValidar.usar ? 'USAR' : 'NO USAR'} severity={resultadoValidar.usar ? 'success' : 'danger'}
                                    icon={resultadoValidar.usar ? 'pi pi-check' : 'pi pi-times'} />
                                <span className="text-sm">{resultadoValidar.mensaje}</span>
                                {resultadoValidar.ultimaPosicion && <span className="text-xs text-color-secondary">· Última posición: {resultadoValidar.ultimaPosicion}</span>}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="col-12" style={{ order: 2 }}>
                <div className="card">
                    <h5 className="mb-1">Historial de posiciones por rango de fecha</h5>
                    <p className="text-sm text-color-secondary mt-0">
                        Detalle de las posiciones de un equipo entre dos fechas, leído en vivo de Traccar 1 y 2. Elegí si el rango se aplica por <b>fecha del dispositivo (fixtime)</b> o <b>fecha del servidor (servertime)</b> — útil para detectar novedades. Máx. {HIST_MAX} filas por consulta.
                    </p>
                    <div className="field mb-3">
                        <label>Dispositivo</label>
                        <Dropdown value={histDispositivo} options={opcionesDispositivos} onChange={(e) => setHistDispositivo(e.value)}
                            filter showClear placeholder="Seleccione un dispositivo..." className="w-full" />
                    </div>
                    <div className="flex align-items-end gap-3 flex-wrap">
                        <div className="field mb-0">
                            <label>Rango por</label>
                            <Dropdown value={histCampo} onChange={(e) => setHistCampo(e.value)} style={{ width: '15rem' }}
                                options={[
                                    { label: 'Fecha dispositivo (fixtime)', value: 'fixtime' },
                                    { label: 'Fecha servidor (servertime)', value: 'servertime' }
                                ]} />
                        </div>
                        <div className="field mb-0">
                            <label>Desde</label>
                            <Calendar value={histInicio} onChange={(e) => setHistInicio(e.value)} showTime showSeconds hourFormat="24"
                                dateFormat="yy-mm-dd" placeholder="Desde" showButtonBar style={{ width: '15rem' }} />
                        </div>
                        <div className="field mb-0">
                            <label>Hasta</label>
                            <Calendar value={histFin} onChange={(e) => setHistFin(e.value)} showTime showSeconds hourFormat="24"
                                dateFormat="yy-mm-dd" placeholder="Hasta" showButtonBar style={{ width: '15rem' }} />
                        </div>
                        <div className="field mb-0">
                            <Button icon="pi pi-search" label="Ver detalle" className="p-button-primary"
                                onClick={verHistorial} loading={histLoading} disabled={!histDispositivo} />
                        </div>
                        <div className="field mb-0">
                            <Button icon="pi pi-map" label="Ver en mapa" className="p-button-help p-button-outlined"
                                onClick={() => { setHistMarkerSel(null); setHistShowMap(true); }}
                                disabled={histPosicionesMapa.length === 0}
                                tooltip="Ver el recorrido en el mapa" tooltipOptions={{ position: 'top' }} />
                        </div>
                        <div className="field mb-0">
                            <Button icon="pi pi-file-excel" label="Excel" className="p-button-success p-button-outlined"
                                onClick={exportarHistorialExcel} disabled={histDetalle.length === 0} />
                        </div>
                        <div className="field mb-0">
                            <Button icon="pi pi-file-pdf" label="PDF" className="p-button-danger p-button-outlined"
                                onClick={exportarHistorialPdf} loading={histPdfLoading}
                                disabled={histDetalle.length === 0}
                                tooltip="Informe con resumen, mapa y detalle" tooltipOptions={{ position: 'top' }} />
                        </div>
                        <div className="field mb-0 flex align-items-center">
                            <Checkbox inputId="histPdfServidor" checked={histPdfServidor}
                                onChange={(e) => setHistPdfServidor(e.checked)} />
                            <label htmlFor="histPdfServidor" className="ml-2 mb-0">Incluir fecha servidor en el PDF</label>
                        </div>
                    </div>

                    {histLimite && (
                        <div className="mt-2">
                            <Tag severity="warning" icon="pi pi-exclamation-triangle"
                                value={`Se alcanzó el límite de ${HIST_MAX} filas. Achicá el rango de fechas para ver el detalle completo.`} />
                        </div>
                    )}

                    {histBuscado && (
                        <DataTable value={histDetalleFiltrado} paginator rows={20} rowsPerPageOptions={[10, 20, 50, 100]}
                            loading={histLoading} emptyMessage="Sin posiciones en el rango para este equipo."
                            responsiveLayout="scroll" className="mt-3" sortField={histCampo} sortOrder={1}
                            header={
                                <div className="flex justify-content-between align-items-center flex-wrap gap-2">
                                    <span className="text-sm text-color-secondary">
                                        {histDetalleFiltrado.length} posición(es){histFiltro ? ` de ${histDetalle.length}` : ''}
                                    </span>
                                    <span className="p-input-icon-left">
                                        <i className="pi pi-search" />
                                        <InputText type="search" value={histFiltro} onChange={(e) => setHistFiltro(e.target.value)} placeholder="Buscar en el historial..." />
                                    </span>
                                </div>
                            }>
                            <Column field="origen" body={origenTpl} header="Origen" sortable style={{ minWidth: '110px' }} />
                            <Column field="uniqueid" header="Identificador" sortable style={{ minWidth: '140px' }} />
                            <Column field="grupoNombre" header="Grupo" sortable style={{ minWidth: '150px' }} />
                            <Column field="servertime" header="Fecha servidor" body={fechaTpl('servertime')} sortable style={{ minWidth: '150px' }} />
                            <Column field="fixtime" header="Fecha dispositivo" body={fechaTpl('fixtime')} sortable style={{ minWidth: '150px' }} />
                            <Column field="desfaseSegundos" header="Desfase" body={desfaseTpl} sortable style={{ minWidth: '110px' }} />
                            <Column field="velocidadKmh" header="Velocidad" body={velocidadTpl} sortable style={{ minWidth: '110px' }} />
                            <Column field="latitude" header="Latitud" body={(r) => coordTpl(r.latitude)} sortable style={{ minWidth: '110px' }} />
                            <Column field="longitude" header="Longitud" body={(r) => coordTpl(r.longitude)} sortable style={{ minWidth: '110px' }} />
                            <Column field="nearpoint" header="Punto cercano" sortable style={{ minWidth: '180px' }} />
                            <Column header="Ubicación" body={mapsTpl} style={{ minWidth: '90px' }} />
                        </DataTable>
                    )}
                </div>
            </div>

            {/* Visor del recorrido del historial: traza la ruta y permite ver el detalle de cada punto. */}
            <Dialog
                header={`Recorrido — ${histDispositivo?.deviceName || histDispositivo?.uniqueid || ''} (${histPosicionesMapa.length} punto(s))`}
                visible={histShowMap} style={{ width: '92vw' }} modal
                onHide={() => { setHistShowMap(false); setHistMarkerSel(null); }}
                onShow={() => setTimeout(histFitBounds, 250)}>
                {histMapLoaded && histPosicionesMapa.length > 0 ? (
                    <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '70vh' }}
                        center={{ lat: Number(histPosicionesMapa[0].latitude), lng: Number(histPosicionesMapa[0].longitude) }}
                        zoom={13}
                        onLoad={(m) => { histMapRef.current = m; setTimeout(histFitBounds, 150); }}>
                        <PolylineF
                            path={histPosicionesMapa.map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }))}
                            options={{ strokeColor: '#1F4E78', strokeOpacity: 0.9, strokeWeight: 4 }} />
                        {/* Inicio y fin bien marcados, como en el informe PDF */}
                        <MarkerF
                            position={{ lat: Number(histPosicionesMapa[0].latitude), lng: Number(histPosicionesMapa[0].longitude) }}
                            label={{ text: 'A', color: '#fff', fontWeight: 'bold' }} title="Inicio del recorrido" />
                        <MarkerF
                            position={{
                                lat: Number(histPosicionesMapa[histPosicionesMapa.length - 1].latitude),
                                lng: Number(histPosicionesMapa[histPosicionesMapa.length - 1].longitude)
                            }}
                            label={{ text: 'B', color: '#fff', fontWeight: 'bold' }} title="Fin del recorrido" />
                        {histPosicionesMapa.map((p, i) => (
                            <MarkerF
                                key={`hp-${i}-${p.latitude}-${p.longitude}`}
                                position={{ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }}
                                opacity={0.01}
                                title={`#${i + 1}`}
                                onClick={() => setHistMarkerSel({ ...p, idx: i + 1 })} />
                        ))}
                        {histMarkerSel && (
                            <InfoWindowF
                                position={{ lat: parseFloat(histMarkerSel.latitude), lng: parseFloat(histMarkerSel.longitude) }}
                                onCloseClick={() => setHistMarkerSel(null)}>
                                <div style={{ padding: 4, maxWidth: 300 }}>
                                    <div className="mb-2"><strong>Punto #{histMarkerSel.idx}</strong></div>
                                    <div><small><strong>Fecha dispositivo:</strong> {histMarkerSel.fixtime ? moment(histMarkerSel.fixtime).format('YYYY-MM-DD HH:mm:ss') : '-'}</small></div>
                                    <div><small><strong>Fecha servidor:</strong> {histMarkerSel.servertime ? moment(histMarkerSel.servertime).format('YYYY-MM-DD HH:mm:ss') : '-'}</small></div>
                                    <div><small><strong>Velocidad:</strong> {histMarkerSel.velocidadKmh != null ? `${Number(histMarkerSel.velocidadKmh).toFixed(0)} km/h` : '-'}</small></div>
                                    <div className="mb-2"><small><strong>Punto cercano:</strong> {histMarkerSel.nearpoint || '-'}</small></div>
                                    {histMarkerSel.mapsUrl && (
                                        <a href={histMarkerSel.mapsUrl} target="_blank" rel="noopener noreferrer">Abrir en Google Maps</a>
                                    )}
                                </div>
                            </InfoWindowF>
                        )}
                    </GoogleMap>
                ) : (
                    <div className="text-center py-5">
                        <i className="pi pi-map-marker text-4xl mb-3 text-400"></i>
                        <p>{histMapLoaded ? 'No hay posiciones con coordenadas válidas para mostrar.' : 'Cargando mapa...'}</p>
                    </div>
                )}
            </Dialog>

            <Dialog header="Configuración del Reporte de Dispositivos" visible={showConfig} style={{ width: '60rem' }} modal onHide={() => setShowConfig(false)}>
                <ReporteDispositivosConfig />
            </Dialog>
        </div>
    );
};

export default ReporteDispositivos;
